import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import {
  type HideInspectionTemplateMutation,
  type HideInspectionTemplateMutationVariables,
} from 'graphql-query-contracts';
import { hideInspectionTemplateMutation } from 'graphql-queries';

import { HideInspectionTemplateError } from './errors/HideInspectionTemplateError';

export type HideInspectionTemplateParams = {
  channelId: string;
  templateId: string;
};

export type HideInspectionTemplateResult = {
  success: boolean;
  newVersion: number;
};

export type UseHideInspectionTemplateResult = {
  hideInspectionTemplate: (
    params: HideInspectionTemplateParams
  ) => Promise<HideInspectionTemplateResult>;
  loading: boolean;
  error: Error | undefined;
};

function validateParams(params: HideInspectionTemplateParams): void {
  if (!params.channelId?.trim()) {
    throw new HideInspectionTemplateError('channelId is required');
  }

  if (!params.templateId?.trim()) {
    throw new HideInspectionTemplateError('templateId is required');
  }
}

export const useHideInspectionTemplate = (): UseHideInspectionTemplateResult => {
  const [hideTemplate, { loading }] = useMutation<
    HideInspectionTemplateMutation,
    HideInspectionTemplateMutationVariables
  >(hideInspectionTemplateMutation);

  const [error, setError] = useState<Error | undefined>(undefined);

  const hideInspectionTemplate = useCallback(
    async (
      params: HideInspectionTemplateParams
    ): Promise<HideInspectionTemplateResult> => {
      try {
        setError(undefined);

        validateParams(params);

        const variables: HideInspectionTemplateMutationVariables = {
          input: {
            channelId: params.channelId,
            templateId: params.templateId,
          },
        };

        const mutationResult = await hideTemplate({ variables });

        const payload = mutationResult.data?.hideInspectionTemplate;

        if (!payload) {
          throw new HideInspectionTemplateError();
        }

        if (!payload.success) {
          throw new HideInspectionTemplateError(
            'Hide inspection template did not succeed'
          );
        }

        return {
          success: payload.success,
          newVersion: payload.newVersion,
        };
      } catch (err) {
        const wrappedError =
          err instanceof HideInspectionTemplateError
            ? err
            : new HideInspectionTemplateError(
                err instanceof Error
                  ? err.message
                  : 'Failed to hide inspection template',
                { cause: err }
              );

        setError(wrappedError);

        throw wrappedError;
      }
    },
    [hideTemplate]
  );

  return {
    hideInspectionTemplate,
    loading,
    error,
  };
};
